import { useState } from "react";
import Modal from "shared/Modal";
import Button from "shared/Button";
import {
  AttendanceRecord,
  AttendanceEmployee,
  adminAddRecord,
  adminEditRecord,
  adminDeleteRecord,
  submitUpdateRequest,
} from "../api/attendanceApi";
import EmployeeSelect from "./EmployeeSelect";
import EmployeeLoadErrorBanner from "./EmployeeLoadErrorBanner";
import { useEmployeeList } from "./useEmployeeList";

const inputStyles =
  "w-full px-3 py-2 bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all disabled:opacity-60";

export type AttendanceAction = "add" | "edit" | "delete";

interface Props {
  writeMode: "admin-direct" | "hr-request";
  action: AttendanceAction;
  record?: AttendanceRecord;
  defaultEmployeeCode?: string;
  defaultStatus?: number;
  defaultDate?: string;
  // Passed by AttendancePage, which already loads this for its own employee
  // filter — reuses it instead of firing a second identical request every
  // time "Add Record" opens. Falls back to fetching its own copy when absent
  // (e.g. no caller happens to have the list already).
  employees?: AttendanceEmployee[];
  onClose: () => void;
  onSuccess: () => void;
}

// Timestamps in this system are stored/displayed as naive local wall-clock
// values (no timezone offset) — slice the string directly rather than routing
// through Date, which would reinterpret it in the browser's zone.
function isoToDatetimeLocal(iso?: string) {
  return iso ? iso.slice(0, 16) : "";
}

const TITLES: Record<AttendanceAction, string> = {
  add: "Add Missing Punch",
  edit: "Edit Attendance Record",
  delete: "Remove Attendance Record",
};

export default function AttendanceActionModal({
  writeMode, action, record, defaultEmployeeCode, defaultStatus, defaultDate, employees: employeesProp, onClose, onSuccess,
}: Props) {
  const [employeeCode, setEmployeeCode] = useState(record?.employee_code || defaultEmployeeCode || "");
  const [status, setStatus] = useState(record?.status ?? defaultStatus ?? 0);
  // datetime-local is minute-precision, but stored punches carry seconds — the
  // original (untouched) timestamp keeps its real seconds; only reconstruct
  // from the input once the user actually changes it (see submittedTimestamp).
  const originalDateTime = isoToDatetimeLocal(record?.timestamp);
  // A datetime-local input rejects a date with no time component (silently
  // renders blank), so a bare defaultDate needs a placeholder time — 09:00 for
  // a missing check-in, 17:00 for a missing check-out — the user adjusts it.
  const [dateTime, setDateTime] = useState(
    originalDateTime || (defaultDate ? `${defaultDate}T${defaultStatus === 1 ? "17:00" : "09:00"}` : ""),
  );
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // Editable only for a from-scratch "Add Record" (no day already picked).
  // When opened from a specific day's detail view (defaultEmployeeCode set —
  // "Add Missing Check-In/Out" for that day), the employee must stay fixed to
  // the day it was opened for; a dropdown there would make it too easy to
  // accidentally file the punch under a different employee.
  const employeeFieldEditable = action === "add" && !defaultEmployeeCode;

  const { employees, loadError: employeesLoadError, retry: loadEmployees } = useEmployeeList(employeeFieldEditable, employeesProp);

  const isHrRequest = writeMode === "hr-request";
  // Editing only the Status field and leaving date/time untouched must not
  // silently truncate the stored timestamp's seconds to :00 — fall back to
  // the original full-precision value whenever the input wasn't changed.
  const submittedTimestamp = record && dateTime === originalDateTime ? record.timestamp : dateTime;

  const handleSubmit = async () => {
    setError("");
    if (!employeeCode.trim()) return setError("Employee code is required.");
    if (action !== "delete" && !dateTime) return setError("Date & time are required.");
    if (isHrRequest && !reason.trim()) return setError("A reason is required.");

    setSubmitting(true);
    try {
      if (writeMode === "admin-direct") {
        if (action === "add") await adminAddRecord(employeeCode.trim(), status, submittedTimestamp, reason || undefined);
        else if (action === "edit") await adminEditRecord(record!.id, status, submittedTimestamp, reason || undefined);
        else await adminDeleteRecord(record!.id, reason || undefined);
      } else {
        // attendance_date identifies which existing record/day this request is
        // about (and gates the 7-day window) — for edit/delete that's the
        // record's current date, not whatever new date the user is correcting
        // it to (that goes through requested_timestamp instead).
        const attendanceDate = action === "add" ? dateTime.slice(0, 10) : record!.date;
        await submitUpdateRequest({
          action,
          employee_code: employeeCode.trim(),
          attendance_date: attendanceDate,
          target_record_id: record?.id,
          requested_status: action !== "delete" ? status : undefined,
          requested_timestamp: action !== "delete" ? submittedTimestamp : undefined,
          reason: reason.trim(),
        });
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Something went wrong.");
    }
    setSubmitting(false);
  };

  return (
    <Modal size="sm" onClick={submitting ? undefined : onClose} closeButtonCLick={submitting ? undefined : onClose} modalClassName="p-6">
      <h2 className="text-lg font-semibold text-foreground font-inter mb-4">
        {isHrRequest ? `Request: ${TITLES[action]}` : TITLES[action]}
      </h2>

      <div className="space-y-3">
        <div>
          <label className="text-xs text-muted-foreground font-inter">Employee</label>
          {employeeFieldEditable ? (
            employeesLoadError ? (
              <EmployeeLoadErrorBanner onRetry={loadEmployees} />
            ) : (
              <EmployeeSelect employees={employees} value={employeeCode} onChange={setEmployeeCode} className="mt-1" />
            )
          ) : (
            <input value={employeeCode} disabled className={`${inputStyles} mt-1`} />
          )}
        </div>

        {action !== "delete" && (
          <>
            <div>
              <label className="text-xs text-muted-foreground font-inter">Status</label>
              <select value={status} onChange={(e) => setStatus(Number(e.target.value))} className={`${inputStyles} mt-1`}>
                <option value={0}>Check In</option>
                <option value={1}>Check Out</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground font-inter">Date &amp; Time</label>
              <input
                type="datetime-local"
                value={dateTime}
                onChange={(e) => setDateTime(e.target.value)}
                className={`${inputStyles} mt-1`}
              />
            </div>
          </>
        )}

        {action === "delete" && record && (
          <p className="text-sm text-foreground font-inter">
            {record.status_label} for <span className="font-medium">{record.employee_name}</span> on {record.date}
          </p>
        )}

        <div>
          <label className="text-xs text-muted-foreground font-inter">
            {isHrRequest ? "Reason (required)" : "Note (optional)"}
          </label>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className={`${inputStyles} mt-1 resize-none`} />
        </div>

        {error && <p className="text-sm text-destructive font-inter">{error}</p>}
      </div>

      <div className="flex items-center justify-end gap-3 mt-6">
        <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button type="button" variant={action === "delete" ? "danger" : "primary"} onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Saving…" : isHrRequest ? "Submit Request" : action === "delete" ? "Delete" : "Save"}
        </Button>
      </div>
    </Modal>
  );
}
